// S27: Invite Status Page - For expired/invalid invites
import { Button, Card, Space, Typography } from 'antd';
import {
  CloseCircleOutlined,
  MailOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';

const { Text, Title } = Typography;

interface InviteStatusPageProps {
  status?: 'expired' | 'invalid' | 'used' | 'not-found';
  message?: string;
}

export function InviteStatusPage({
  status: propStatus,
  message: propMessage,
}: InviteStatusPageProps = {}) {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const statusParam = searchParams.get('status') as 'expired' | 'invalid' | 'used' | 'not-found' | null;
  
  // Use prop status or query param status
  const status = propStatus || statusParam || 'invalid';
  const message = propMessage;

  const getStatusConfig = () => {
    switch (status) {
      case 'expired':
        return {
          icon: <CloseCircleOutlined style={{ fontSize: '72px', color: '#ff4d4f' }} />,
          title: 'Invite Expired',
          description:
            message || 'This invite link has expired and is no longer valid.',
        };
      case 'used':
        return {
          icon: <InfoCircleOutlined style={{ fontSize: '72px', color: '#1890ff' }} />,
          title: 'Invite Already Used',
          description:
            message || 'This invite has already been accepted by another user.',
        };
      case 'not-found':
        return {
          icon: <CloseCircleOutlined style={{ fontSize: '72px', color: '#ff4d4f' }} />,
          title: 'Invite Not Found',
          description:
            message || 'No invite was found with this code. Please check your link.',
        };
      default:
        return {
          icon: <CloseCircleOutlined style={{ fontSize: '72px', color: '#ff4d4f' }} />,
          title: 'Invalid Invite Link',
          description:
            message || 'This invite link is not valid or has been revoked.',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        backgroundColor: '#141414',
      }}
    >
      <Card
        style={{
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <div>{config.icon}</div>

          <div>
            <Title level={2} style={{ marginBottom: '8px' }}>
              {config.title}
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              {config.description}
            </Text>
          </div>

          {code && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Invite Code: <Text code>{code}</Text>
            </Text>
          )}

          <div
            style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '8px',
            }}
          >
            <Space orientation="vertical" size="small">
              <MailOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <Text strong>Need a new invite?</Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Contact the admin of DivvyStash to request a new invite
                link.
              </Text>
            </Space>
          </div>

          <Button
            type="link"
            href="/login"
            style={{ marginTop: '16px' }}
          >
            Already have an account? Log in
          </Button>
        </Space>
      </Card>
    </div>
  );
}
